export type ParsedSystemBranchRouteTail = {
  desiredBranchName: string | null;
  shortCommitId: string | null;
  isBranchHistoryList: boolean;
  treeSplat: string;
};

export type SystemBranchTreeRouteScope = {
  systemId: string;
  branchName: string;
  shortCommitId?: string | null;
};

const emptyBranchRouteTail: ParsedSystemBranchRouteTail = {
  desiredBranchName: null,
  shortCommitId: null,
  isBranchHistoryList: false,
  treeSplat: "",
};

export function encodeBranchSegments(name: string): string {
  return name.split("/").map(encodeURIComponent).join("/");
}

export function parseSystemBranchRouteTail(segments: string[]): ParsedSystemBranchRouteTail {
  const { routeSegments, treeSplat } = splitTreeSplat(segments);
  return parseBranchRouteSegments(routeSegments, treeSplat);
}

export function buildSystemBranchRoute(scope: SystemBranchTreeRouteScope): string {
  const branchPath = encodeBranchSegments(scope.branchName);
  const historyPath = scope.shortCommitId ? `/history/${scope.shortCommitId}` : "";
  return `/systems/${scope.systemId}/${branchPath}${historyPath}`;
}

export function buildSystemBranchTreeRoute(scope: SystemBranchTreeRouteScope, pathId?: string): string {
  const treeBase = `${buildSystemBranchRoute(scope)}/tree`;
  return pathId ? `${treeBase}/${pathId}` : treeBase;
}

function isCommitIdSegment(segment: string): boolean {
  return /^(?:[0-9a-f]{6,40}|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i.test(segment);
}

function decodeBranchSegments(segments: string[]): string | null {
  if (segments.length === 0) {
    return null;
  }

  return segments.map(decodeURIComponent).join("/");
}

function splitTreeSplat(segments: string[]): { routeSegments: string[]; treeSplat: string } {
  const treeIndex = segments.lastIndexOf("tree");
  if (treeIndex === -1) {
    return { routeSegments: segments, treeSplat: "" };
  }
  return {
    routeSegments: segments.slice(0, treeIndex),
    treeSplat: segments.slice(treeIndex).join("/"),
  };
}

function parseBranchRouteSegments(segments: string[], treeSplat: string): ParsedSystemBranchRouteTail {
  if (segments.length === 0) {
    return { ...emptyBranchRouteTail, treeSplat };
  }

  const lastSegment = segments[segments.length - 1];
  if (lastSegment === "history") {
    const branchSegments = segments.slice(0, -1);
    return {
      desiredBranchName: decodeBranchSegments(branchSegments),
      shortCommitId: null,
      isBranchHistoryList: true,
      treeSplat,
    };
  }

  const maybeCommitId = lastSegment;
  const maybeHistory = segments[segments.length - 2];
  const maybeLegacyHistory = segments[segments.length - 3];
  if (maybeLegacyHistory === "history" && maybeCommitId && isCommitIdSegment(maybeCommitId)) {
    const branchSegments = segments.slice(0, -3);
    return {
      desiredBranchName: decodeBranchSegments(branchSegments),
      shortCommitId: maybeCommitId,
      isBranchHistoryList: false,
      treeSplat,
    };
  }

  if (maybeHistory === "history" && maybeCommitId && isCommitIdSegment(maybeCommitId)) {
    const branchSegments = segments.slice(0, -2);
    return {
      desiredBranchName: decodeBranchSegments(branchSegments),
      shortCommitId: maybeCommitId,
      isBranchHistoryList: false,
      treeSplat,
    };
  }

  return {
    desiredBranchName: decodeBranchSegments(segments),
    shortCommitId: null,
    isBranchHistoryList: false,
    treeSplat,
  };
}
